import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Building2, Plus, Search, X, Phone, Mail, Globe, Tag,
  ChevronRight, Briefcase, UserPlus, Trash2, Edit3, Save, Star,
  AlertTriangle, StickyNote, ExternalLink, MapPin, Camera,
  ArrowUpDown, Check, ChevronDown
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useClients } from '../../hooks/useClients';
import { useOrganization } from '../../hooks/useOrganization';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import type { Client, ClientContact } from '../../types';
import { CompanyAutocomplete, type CompanyResult } from '../shared/CompanyAutocomplete';

// ── Priority badge ──────────────────────────────────────────────────────────
const priorityConfig = {
  haute:   { label: 'Haute', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
  moyenne: { label: 'Moyenne', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  basse:   { label: 'Basse', bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', dot: 'bg-slate-400' },
};

// ── Catégorie entreprise (Annuaire des Entreprises) ─────────────────────────
const categorieConfig: Record<string, { label: string; color: string }> = {
  GE:  { label: 'Grande Entreprise', color: 'bg-indigo-50 text-indigo-600 border border-indigo-100' },
  ETI: { label: 'ETI',               color: 'bg-violet-50 text-violet-600 border border-violet-100' },
  PME: { label: 'PME',               color: 'bg-emerald-50 text-emerald-600 border border-emerald-100' },
  MI:  { label: 'Micro',             color: 'bg-slate-100 text-slate-500 border border-slate-200' },
};

// ── NAF division → sector mapping ────────────────────────────────────────────
const NAF_TO_SECTOR: Record<string, string> = {
  '10': 'Agroalimentaire', '11': 'Agroalimentaire', '12': 'Agroalimentaire',
  '13': 'Agroalimentaire', '14': 'Agroalimentaire', '15': 'Agroalimentaire', '16': 'Agroalimentaire',
  '26': 'Industrie & Manufacturing', '27': 'Industrie & Manufacturing',
  '28': 'Industrie & Manufacturing', '29': 'Industrie & Manufacturing', '30': 'Industrie & Manufacturing',
  '35': 'Énergie & Utilities',
  '41': 'BTP & Construction', '42': 'BTP & Construction', '43': 'BTP & Construction',
  '45': 'Distribution & Retail', '46': 'Distribution & Retail', '47': 'Distribution & Retail',
  '49': 'Transport & Logistique', '50': 'Transport & Logistique', '51': 'Transport & Logistique',
  '52': 'Transport & Logistique', '53': 'Transport & Logistique',
  '58': 'Médias & Communication', '59': 'Médias & Communication', '60': 'Médias & Communication',
  '61': 'Télécommunications',
  '62': 'Technologie & Logiciels', '63': 'Technologie & Logiciels',
  '64': 'Finance & Banque', '65': 'Finance & Banque',
  '66': 'Assurance',
  '68': 'Immobilier',
  '69': 'Conseil & Services', '70': 'Conseil & Services', '71': 'Conseil & Services',
  '72': 'Conseil & Services', '73': 'Conseil & Services', '74': 'Conseil & Services',
  '75': 'Défense & Aérospatial',
  '84': 'Secteur Public',
  '85': 'Éducation & Formation',
  '86': 'Santé & Pharma', '87': 'Santé & Pharma', '88': 'Santé & Pharma',
};

function nafToSector(nafCode: string): string {
  const division = nafCode.replace(/\./g, '').slice(0, 2);
  return NAF_TO_SECTOR[division] ?? '';
}

// ── Predefined sectors ────────────────────────────────────────────────────────
const SECTORS = [
  'Technologie & Logiciels', 'Conseil & Services', 'Finance & Banque',
  'Assurance', 'Médias & Communication', 'Industrie & Manufacturing',
  'Santé & Pharma', 'Distribution & Retail', 'Énergie & Utilities',
  'Télécommunications', 'Immobilier', 'Transport & Logistique',
  'Agroalimentaire', 'Luxe & Mode', 'Éducation & Formation',
  'Secteur Public', 'BTP & Construction', 'Défense & Aérospatial',
];

function SectorInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const filtered = value.trim()
    ? SECTORS.filter(s => s.toLowerCase().includes(value.toLowerCase()))
    : SECTORS;

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <input
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none"
        placeholder="Ex: Technologie & Logiciels, Médias…"
      />
      {open && filtered.length > 0 && (
        <ul className="absolute z-50 mt-1.5 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-h-52 overflow-y-auto">
          {filtered.map(s => (
            <li key={s}>
              <button
                type="button"
                onMouseDown={() => { onChange(s); setOpen(false); }}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-amber-50 ${value === s ? 'text-amber-700 font-medium bg-amber-50/60' : 'text-slate-700'}`}
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Logo component ────────────────────────────────────────────────────────────
function ClientLogo({ client, size }: { client: Client; size: 'sm' | 'lg' }) {
  const [error, setError] = useState(false);
  useEffect(() => { setError(false); }, [client.logo_url]);
  const logoUrl = !error && client.logo_url ? client.logo_url : null;
  const initial = client.short_name
    ? client.short_name.slice(0, 2).toUpperCase()
    : client.name.charAt(0).toUpperCase();

  function handleLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    if (e.currentTarget.naturalWidth < 2) setError(true);
  }

  if (size === 'lg') {
    return (
      <div
        className="w-14 h-14 rounded-xl overflow-hidden shrink-0 shadow-lg flex items-center justify-center"
        style={logoUrl ? { background: '#f8fafc', border: '1px solid #e2e8f0' } : { background: 'linear-gradient(135deg, #f59e0b, #ea580c)' }}
      >
        {logoUrl
          ? <img src={logoUrl} alt={client.name} className="w-full h-full object-contain p-1.5" onError={() => setError(true)} onLoad={handleLoad} />
          : <span className="text-white text-xl font-bold">{initial}</span>
        }
      </div>
    );
  }

  return (
    <div
      className="w-10 h-10 rounded-lg overflow-hidden shrink-0 flex items-center justify-center transition-all"
      style={logoUrl ? { background: '#f8fafc', border: '1px solid #e2e8f0' } : { background: 'linear-gradient(135deg, #475569, #334155)' }}
    >
      {logoUrl
        ? <img src={logoUrl} alt={client.name} className="w-full h-full object-contain p-1" onError={() => setError(true)} onLoad={handleLoad} />
        : <span className="text-white text-sm font-bold">{initial}</span>
      }
    </div>
  );
}

// ── Create Client Modal ─────────────────────────────────────────────────────
function CreateClientModal({ onClose, onCreate }: {
  onClose: () => void;
  onCreate: (data: {
    name: string;
    sector?: string;
    city?: string;
    website?: string;
    notes?: string;
    siren?: string;
    naf_code?: string;
    categorie_entreprise?: string;
    date_creation_entreprise?: string;
  }) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [siren, setSiren] = useState('');
  const [nafCode, setNafCode] = useState('');
  const [categorieEntreprise, setCategorieEntreprise] = useState('');
  const [dateCreationEntreprise, setDateCreationEntreprise] = useState('');
  const [sector, setSector] = useState('');
  const [city, setCity] = useState('');
  const [website, setWebsite] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleCompanySelect = (company: CompanyResult) => {
    if (company.etat_administratif === 'F') {
      toast.warning(`${company.nom_complet} est une société radiée. Vérifiez avant de continuer.`);
    }
    setName(company.nom_complet);
    setSiren(company.siren);
    setNafCode(company.activite_principale ?? '');
    setCategorieEntreprise(company.categorie_entreprise ?? '');
    setDateCreationEntreprise(company.date_creation ?? '');
    setSector(company.libelle_activite_principale || nafToSector(company.activite_principale ?? ''));
    // Ville depuis le siège
    const commune = company.siege?.commune ?? '';
    setCity(commune ? commune.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onCreate({
        name: name.trim(),
        sector: sector.trim() || undefined,
        city: city.trim() || undefined,
        website: website.trim() || undefined,
        notes: notes.trim() || undefined,
        siren: siren || undefined,
        naf_code: nafCode || undefined,
        categorie_entreprise: categorieEntreprise || undefined,
        date_creation_entreprise: dateCreationEntreprise || undefined,
      });
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Building2 size={20} className="text-amber-600" /> Nouveau Client
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nom de l'entreprise *</label>
            <CompanyAutocomplete onSelect={handleCompanySelect} placeholder="Ex: Canal+, Capgemini…" />
            {siren && (
              <p className="mt-1 text-xs text-slate-400 font-mono">SIREN {siren.replace(/(\d{3})(?=\d)/g, '$1 ')}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Secteur</label>
            <SectorInput value={sector} onChange={setSector} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ville</label>
              <input value={city} onChange={e => setCity(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none" placeholder="Ex: Paris" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Site web</label>
              <input value={website} onChange={e => setWebsite(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none" placeholder="https://..." />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none resize-none" placeholder="Contexte commercial, historique..." />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">Annuler</button>
            <button type="submit" disabled={!name.trim() || saving}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-xl transition-colors disabled:opacity-50 flex items-center gap-2">
              {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Plus size={16} />}
              Créer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Mission status config ────────────────────────────────────────────────────
const statusMissionConfig: Record<string, { label: string; color: string }> = {
  'Ouvert':    { label: 'Ouvert',    color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  'En Pause':  { label: 'En Pause',  color: 'bg-amber-50 text-amber-700 border-amber-200' },
  'Brouillon': { label: 'Brouillon', color: 'bg-slate-100 text-slate-600 border-slate-200' },
  'Fermé':     { label: 'Fermé',     color: 'bg-red-50 text-red-600 border-red-200' },
};

interface ClientMission { id: string; title: string; status: string; created_at: string; }

// ── Edit Client Modal ────────────────────────────────────────────────────────
function EditClientModal({ client, onClose, onSave }: {
  client: Client;
  onClose: () => void;
  onSave: (updates: Partial<Client>) => Promise<void>;
}) {
  const [name, setName] = useState(client.name);
  const [siren, setSiren] = useState(client.siren ?? '');
  const [nafCode, setNafCode] = useState(client.naf_code ?? '');
  const [categorieEntreprise, setCategorieEntreprise] = useState(client.categorie_entreprise ?? '');
  const [dateCreation, setDateCreation] = useState(client.date_creation_entreprise ?? '');
  const [sector, setSector] = useState(client.sector ?? '');
  const [city, setCity] = useState(client.city ?? '');
  const [website, setWebsite] = useState(client.website ?? '');
  const [notes, setNotes] = useState(client.notes ?? '');
  const [saving, setSaving] = useState(false);

  const handleCompanySelect = (company: CompanyResult) => {
    if (company.etat_administratif === 'F') toast.warning(`${company.nom_complet} est une société radiée.`);
    setName(company.nom_complet);
    setSiren(company.siren);
    setNafCode(company.activite_principale ?? '');
    setCategorieEntreprise(company.categorie_entreprise ?? '');
    setDateCreation(company.date_creation ?? '');
    setSector(company.libelle_activite_principale || nafToSector(company.activite_principale ?? ''));
    const commune = company.siege?.commune ?? '';
    if (commune) setCity(commune.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        sector: sector.trim() || undefined,
        city: city.trim() || undefined,
        website: website.trim() || undefined,
        notes: notes.trim() || undefined,
        siren: siren || undefined,
        naf_code: nafCode || undefined,
        categorie_entreprise: categorieEntreprise || undefined,
        date_creation_entreprise: dateCreation || undefined,
      });
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Edit3 size={20} className="text-amber-600" /> Modifier le client
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nom de l'entreprise *</label>
            <CompanyAutocomplete onSelect={handleCompanySelect} defaultValue={client.name} />
            {siren && <p className="mt-1 text-xs text-slate-400 font-mono">SIREN {siren.replace(/(\d{3})(?=\d)/g, '$1 ')}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Secteur</label>
            <SectorInput value={sector} onChange={setSector} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ville</label>
              <input value={city} onChange={e => setCity(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none" placeholder="Ex: Paris" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Site web</label>
              <input value={website} onChange={e => setWebsite(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none" placeholder="https://..." />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none resize-none" placeholder="Contexte commercial, historique..." />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">Annuler</button>
            <button type="submit" disabled={!name.trim() || saving}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-xl transition-colors disabled:opacity-50 flex items-center gap-2">
              {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={16} />}
              Mettre à jour
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Contact Row ─────────────────────────────────────────────────────────────
function ContactRow({ contact, onDelete, onEdit }: {
  contact: ClientContact;
  onDelete: (id: string) => void;
  onEdit: (id: string, updates: Partial<ClientContact>) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(contact.full_name);
  const [role, setRole] = useState(contact.role ?? '');
  const [email, setEmail] = useState(contact.email ?? '');
  const [phone, setPhone] = useState(contact.phone ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onEdit(contact.id, { full_name: name, role, email, phone });
      setEditing(false);
    } catch { /* handled upstream */ }
    finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="border border-indigo-100 rounded-xl p-3 bg-indigo-50/30 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Nom *" autoFocus
            className="px-2.5 py-2 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-indigo-400 outline-none" />
          <input value={role} onChange={e => setRole(e.target.value)} placeholder="Fonction"
            className="px-2.5 py-2 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-indigo-400 outline-none" />
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" type="email"
            className="px-2.5 py-2 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-indigo-400 outline-none" />
          <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Téléphone"
            className="px-2.5 py-2 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-indigo-400 outline-none" />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-xs text-slate-500 hover:bg-white rounded-lg">Annuler</button>
          <button onClick={handleSave} disabled={!name.trim() || saving}
            className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 flex items-center gap-1">
            {saving ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={12} />} Enregistrer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-slate-50 group transition-colors">
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
        style={{ background: 'linear-gradient(135deg, #6366f1, #9333ea)' }}
      >
        {contact.full_name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-900 truncate">{contact.full_name}</span>
          {contact.is_primary && <Star size={12} className="text-amber-500 fill-amber-500 shrink-0" />}
        </div>
        {contact.role && <p className="text-xs text-slate-500 truncate">{contact.role}</p>}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {contact.email && (
          <a href={`mailto:${contact.email}`} className="p-1.5 rounded-md text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors" title={contact.email}>
            <Mail size={14} />
          </a>
        )}
        {contact.phone && (
          <a href={`tel:${contact.phone}`} className="p-1.5 rounded-md text-slate-400 hover:text-green-600 hover:bg-green-50 transition-colors" title={contact.phone}>
            <Phone size={14} />
          </a>
        )}
        <button onClick={() => setEditing(true)}
          className="p-1.5 rounded-md text-slate-300 hover:text-indigo-500 hover:bg-indigo-50 transition-colors opacity-0 group-hover:opacity-100">
          <Edit3 size={14} />
        </button>
        <button onClick={() => onDelete(contact.id)}
          className="p-1.5 rounded-md text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// ── Add Contact Inline ──────────────────────────────────────────────────────
function AddContactForm({ clientId, orgId, onAdd }: {
  clientId: string; orgId: string;
  onAdd: (c: Omit<ClientContact, 'id'|'created_at'|'updated_at'>) => Promise<any>;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onAdd({ client_id: clientId, organization_id: orgId, full_name: name, role, email, phone, is_primary: false });
      setName(''); setRole(''); setEmail(''); setPhone('');
      setOpen(false);
      toast.success('Contact ajouté');
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  if (!open) return (
    <button onClick={() => setOpen(true)} className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 py-1.5 px-2 rounded-lg hover:bg-indigo-50 transition-colors">
      <UserPlus size={14} /> Ajouter un contact
    </button>
  );

  return (
    <div className="border border-indigo-100 rounded-xl p-3 bg-indigo-50/30 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Nom *" autoFocus
          className="px-2.5 py-2 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-indigo-400 outline-none" />
        <input value={role} onChange={e => setRole(e.target.value)} placeholder="Fonction"
          className="px-2.5 py-2 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-indigo-400 outline-none" />
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" type="email"
          className="px-2.5 py-2 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-indigo-400 outline-none" />
        <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Téléphone"
          className="px-2.5 py-2 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-indigo-400 outline-none" />
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-xs text-slate-500 hover:bg-white rounded-lg">Annuler</button>
        <button onClick={handleSave} disabled={!name.trim() || saving}
          className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 flex items-center gap-1">
          {saving ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={12} />} Enregistrer
        </button>
      </div>
    </div>
  );
}

// ── Client Detail Panel (Full Page) ─────────────────────────────────────────
function ClientDetailPanel({ client, onClose, onDelete, onUpdate, onDeleteContact, onAddContact, onEditContact }: {
  client: Client;
  onClose: () => void;
  onDelete: (id: string) => Promise<void>;
  onUpdate: (id: string, updates: Partial<Client>) => Promise<void>;
  onDeleteContact: (id: string) => Promise<void>;
  onAddContact: (c: Omit<ClientContact, 'id'|'created_at'|'updated_at'>) => Promise<any>;
  onEditContact: (id: string, updates: Partial<ClientContact>) => Promise<void>;
}) {
  const navigate = useNavigate();
  const p = client.priority ? priorityConfig[client.priority] : null;
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [editingShortName, setEditingShortName] = useState(false);
  const [shortNameInput, setShortNameInput] = useState(client.short_name ?? '');
  const [showEdit, setShowEdit] = useState(false);
  const [missions, setMissions] = useState<ClientMission[]>([]);
  const [loadingMissions, setLoadingMissions] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setShortNameInput(client.short_name ?? ''); }, [client.short_name]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingMissions(true);
      try {
        const { data } = await supabase
          .from('missions')
          .select('id, mission_title, summary_json, created_at')
          .eq('client_id', client.id)
          .order('created_at', { ascending: false })
          .limit(5);
        if (cancelled) return;
        setMissions((data || []).map((m: any) => ({
          id: m.id,
          title: m.mission_title || m.summary_json?.title || 'Mission sans titre',
          status: m.summary_json?.status || 'Brouillon',
          created_at: m.created_at,
        })));
      } catch { /* silent */ }
      finally { if (!cancelled) setLoadingMissions(false); }
    }
    load();
    return () => { cancelled = true; };
  }, [client.id]);

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const ext = file.name.split('.').pop() ?? 'png';
      const path = `${client.organization_id}/${client.id}.${ext}`;
      const { error } = await supabase.storage.from('client-logos').upload(path, file, { upsert: true });
      if (error) throw error;
      const { data } = supabase.storage.from('client-logos').getPublicUrl(path);
      await onUpdate(client.id, { logo_url: `${data.publicUrl}?t=${Date.now()}` });
      toast.success('Logo mis à jour');
    } catch {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  }

  async function handleRemoveLogo() {
    await onUpdate(client.id, { logo_url: null });
    toast.success('Logo supprimé');
  }

  async function handleSaveShortName() {
    await onUpdate(client.id, { short_name: shortNameInput.trim() || null });
    setEditingShortName(false);
    toast.success('Initiales mises à jour');
  }

  return (
    <div className="animate-in fade-in duration-300 pb-20">
      {/* Header card — bloc unique : ✕ + nom + actions en haut, détails en dessous */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <button
              onClick={onClose}
              className="flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors shrink-0"
              title="Fermer la fiche"
              aria-label="Fermer la fiche"
            >
              <X size={18} />
            </button>
            {/* Avatar — click to upload logo */}
            <div className="flex flex-col items-center gap-1.5">
              <div className="relative group">
                <div className="cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                  <ClientLogo client={client} size="lg" />
                  <div className="absolute inset-0 rounded-xl bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    {uploadingLogo
                      ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      : <Camera size={18} className="text-white" />
                    }
                  </div>
                </div>
                {client.logo_url && (
                  <button type="button" onClick={handleRemoveLogo}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-slate-200 shadow flex items-center justify-center text-slate-400 hover:text-red-500 hover:border-red-300 transition-colors z-10"
                    title="Supprimer le logo">
                    <X size={10} />
                  </button>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
              </div>
              {editingShortName ? (
                <div className="flex items-center gap-1">
                  <input value={shortNameInput}
                    onChange={e => setShortNameInput(e.target.value.slice(0, 3).toUpperCase())}
                    className="w-14 px-2 py-1 text-xs border border-slate-200 rounded-lg outline-none focus:ring-1 focus:ring-amber-400 text-center font-mono uppercase"
                    placeholder="LV" autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') handleSaveShortName(); if (e.key === 'Escape') setEditingShortName(false); }} />
                  <button onClick={handleSaveShortName} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded-md"><Save size={12} /></button>
                  <button onClick={() => setEditingShortName(false)} className="p-1 text-slate-400 hover:bg-slate-100 rounded-md"><X size={12} /></button>
                </div>
              ) : (
                <button onClick={() => setEditingShortName(true)} className="text-[11px] text-slate-400 hover:text-slate-600 flex items-center gap-1 transition-colors">
                  <Edit3 size={10} /> Initiales
                </button>
              )}
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-slate-900 truncate leading-tight mb-1">{client.name}</h2>
              <div className="flex items-center gap-2 flex-wrap">
                {client.sector && <span className="text-sm text-slate-500">{client.sector}</span>}
                {p && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${p.bg} ${p.text} border ${p.border}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`} /> {p.label}
                  </span>
                )}
                {client.categorie_entreprise && categorieConfig[client.categorie_entreprise] && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${categorieConfig[client.categorie_entreprise].color}`}>
                    {categorieConfig[client.categorie_entreprise].label}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                {client.siren && <span className="text-xs text-slate-400 font-mono">SIREN {client.siren.replace(/(\d{3})(?=\d)/g, '$1 ')}</span>}
                {client.city && (
                  <span className="flex items-center gap-1 text-xs text-slate-400">
                    <MapPin size={11} className="shrink-0" />{client.city}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3 shrink-0">
            <div className="flex items-center gap-2">
              <button onClick={() => setShowEdit(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 hover:text-slate-700 transition-colors">
                <Edit3 size={14} /> Modifier
              </button>
              <button onClick={async () => {
                if (window.confirm(`Supprimer le client "${client.name}" et tous ses contacts ?`)) {
                  await onDelete(client.id);
                  onClose();
                  toast.success('Client supprimé');
                }
              }} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-500 bg-red-50 border border-red-100 rounded-lg hover:bg-red-100 transition-colors">
                <Trash2 size={14} /> Supprimer
              </button>
            </div>
            <div className="flex items-center gap-5 text-sm text-slate-600">
              <div className="flex items-center gap-1.5">
                <Briefcase size={14} className="text-amber-600" />
                <span className="font-semibold">{client.mission_count || 0}</span> mission{(client.mission_count || 0) > 1 ? 's' : ''}
              </div>
              <div className="flex items-center gap-1.5">
                <Phone size={14} className="text-indigo-500" />
                <span className="font-semibold">{client.contacts?.length || 0}</span> contact{(client.contacts?.length || 0) > 1 ? 's' : ''}
              </div>
              {client.website && (
                <a href={client.website.startsWith('http') ? client.website : `https://${client.website}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 transition-colors">
                  <Globe size={14} /> Site web <ExternalLink size={10} />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Contacts */}
        <div className="lg:col-span-2">
          <section className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Phone size={15} className="text-indigo-500" /> Contacts
            </h3>
            <div className="space-y-1">
              {(client.contacts || []).length === 0 && (
                <p className="text-sm text-slate-400 italic py-2">Aucun contact ajouté</p>
              )}
              {(client.contacts || []).map(c => (
                <ContactRow key={c.id} contact={c} onDelete={onDeleteContact} onEdit={onEditContact} />
              ))}
            </div>
            <div className="mt-3">
              <AddContactForm clientId={client.id} orgId={client.organization_id} onAdd={onAddContact} />
            </div>
          </section>
        </div>

        {/* Right: Missions + meta */}
        <div className="space-y-4">
          {/* Missions actives */}
          <section className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <Briefcase size={15} className="text-amber-600" /> Missions
              </h3>
              <button onClick={() => navigate(`/missions?client=${client.id}`)}
                className="text-xs text-indigo-500 hover:text-indigo-700 transition-colors">
                Voir tout →
              </button>
            </div>
            {loadingMissions ? (
              <div className="flex justify-center py-4">
                <div className="w-5 h-5 border-2 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
              </div>
            ) : missions.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-1">Aucune mission associée</p>
            ) : (
              <div className="space-y-2">
                {missions.map(m => {
                  const sc = statusMissionConfig[m.status] ?? statusMissionConfig['Brouillon'];
                  return (
                    <button key={m.id} onClick={() => navigate(`/missions/${m.id}`)}
                      className="w-full text-left p-3 rounded-xl border border-slate-200 hover:border-amber-300 hover:bg-amber-50/30 transition-all">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-xs font-medium text-slate-800 leading-tight line-clamp-2 flex-1">{m.title}</p>
                        <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${sc.color}`}>{sc.label}</span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1">
                        {new Date(m.created_at).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {/* Infos légales */}
          {(client.siren || client.naf_code || client.date_creation_entreprise) && (
            <section className="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Building2 size={15} className="text-slate-400" /> Infos légales
              </h3>
              <dl className="space-y-2 text-xs">
                {client.siren && (
                  <div className="flex justify-between">
                    <dt className="text-slate-400">SIREN</dt>
                    <dd className="font-mono text-slate-700">{client.siren.replace(/(\d{3})(?=\d)/g, '$1 ')}</dd>
                  </div>
                )}
                {client.naf_code && (
                  <div className="flex justify-between">
                    <dt className="text-slate-400">Code NAF</dt>
                    <dd className="font-mono text-slate-700">{client.naf_code}</dd>
                  </div>
                )}
                {client.date_creation_entreprise && (
                  <div className="flex justify-between">
                    <dt className="text-slate-400">Création</dt>
                    <dd className="text-slate-700">
                      {new Date(client.date_creation_entreprise).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </dd>
                  </div>
                )}
              </dl>
            </section>
          )}
          {client.notes && (
            <section className="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <StickyNote size={15} className="text-amber-500" /> Notes
              </h3>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{client.notes}</p>
            </section>
          )}
          {client.tags && client.tags.length > 0 && (
            <section className="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Tag size={15} className="text-emerald-500" /> Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {client.tags.map((tag, i) => (
                  <span key={i} className="px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-lg border border-emerald-100">{tag}</span>
                ))}
              </div>
            </section>
          )}
          <p className="text-xs text-slate-400 px-1">
            Créé le {client.created_at ? new Date(client.created_at).toLocaleDateString('fr-FR') : '—'}
          </p>
        </div>
      </div>

      {showEdit && (
        <EditClientModal
          client={client}
          onClose={() => setShowEdit(false)}
          onSave={async (updates) => {
            await onUpdate(client.id, updates);
            toast.success('Client mis à jour');
          }}
        />
      )}
    </div>
  );
}

// ── Client Card ─────────────────────────────────────────────────────────────
function ClientCard({ client, isActive, onClick }: { client: Client; isActive: boolean; onClick: () => void }) {
  const p = client.priority ? priorityConfig[client.priority] : null;
  const cat = client.categorie_entreprise ? categorieConfig[client.categorie_entreprise] : null;
  const primaryContact = (client.contacts || []).find(c => c.is_primary) || (client.contacts || [])[0];

  return (
    <button onClick={onClick}
      className={`w-full text-left p-4 rounded-xl border transition-all duration-200 group ${
        isActive
          ? 'bg-amber-50 border-amber-300 shadow-md shadow-amber-100/50 ring-1 ring-amber-300'
          : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md hover:-translate-y-0.5'
      }`}>
      <div className="flex items-start gap-3">
        <ClientLogo client={client} size="sm" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-900 truncate">{client.name}</h3>
            {p && <span className={`w-2 h-2 rounded-full ${p.dot} shrink-0`} title={`Priorité ${p.label}`} />}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            {client.sector && <p className="text-xs text-slate-500 truncate">{client.sector}</p>}
            {cat && (
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${cat.color}`}>
                {cat.label}
              </span>
            )}
          </div>
          {client.city && (
            <p className="flex items-center gap-1 text-xs text-slate-400 mt-0.5">
              <MapPin size={10} className="shrink-0" />{client.city}
            </p>
          )}
          <div className="flex items-center gap-3 mt-2">
            <span className="flex items-center gap-1 text-xs text-slate-400">
              <Briefcase size={11} /> {client.mission_count || 0}
            </span>
            <span className="flex items-center gap-1 text-xs text-slate-400">
              <Phone size={11} /> {(client.contacts || []).length}
            </span>
            {primaryContact && (
              <span className="text-xs text-slate-400 truncate">{primaryContact.full_name}</span>
            )}
          </div>
        </div>
        <ChevronRight size={16} className={`shrink-0 mt-1 transition-transform ${isActive ? 'text-amber-600' : 'text-slate-300 group-hover:translate-x-0.5'}`} />
      </div>
    </button>
  );
}

// ── Sector filter dropdown ────────────────────────────────────────────────────
function SectorFilterDropdown({ selected, onChange }: { selected: string[]; onChange: (v: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function h(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const toggle = (s: string) =>
    onChange(selected.includes(s) ? selected.filter(x => x !== s) : [...selected, s]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
          selected.length > 0
            ? 'bg-amber-50 text-amber-700 border-amber-300'
            : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
        }`}
      >
        Secteur
        {selected.length > 0 && (
          <span className="text-xs ml-1 font-bold">({selected.length})</span>
        )}
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-2 w-60 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-1 overflow-hidden">
          <div className="max-h-64 overflow-y-auto">
            {SECTORS.map(s => (
              <div key={s} onClick={() => toggle(s)}
                className={`flex items-center justify-between p-2 rounded text-sm cursor-pointer hover:bg-slate-50 ${selected.includes(s) ? 'bg-amber-50 text-amber-700 font-medium' : 'text-slate-600'}`}>
                <span>{s}</span>
                {selected.includes(s) && <Check size={14} />}
              </div>
            ))}
          </div>
          {selected.length > 0 && (
            <div className="border-t border-slate-100 mt-1 pt-1">
              <button onClick={() => onChange([])} className="w-full text-left px-2 py-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors">
                Tout décocher
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Size filter dropdown ──────────────────────────────────────────────────────
function SizeFilterDropdown({ selected, onChange }: { selected: string[]; onChange: (v: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function h(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const SIZES = [
    { code: 'GE', label: 'Grande Entreprise' },
    { code: 'ETI', label: 'ETI' },
    { code: 'PME', label: 'PME' },
    { code: 'MI', label: 'Micro' },
  ];

  const toggle = (s: string) =>
    onChange(selected.includes(s) ? selected.filter(x => x !== s) : [...selected, s]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
          selected.length > 0
            ? 'bg-amber-50 text-amber-700 border-amber-300'
            : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
        }`}
      >
        Taille
        {selected.length > 0 && (
          <span className="text-xs ml-1 font-bold">({selected.length})</span>
        )}
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-2 w-52 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-1">
          {SIZES.map(s => (
            <div key={s.code} onClick={() => toggle(s.code)}
              className={`flex items-center justify-between p-2 rounded text-sm cursor-pointer hover:bg-slate-50 ${selected.includes(s.code) ? 'bg-amber-50 text-amber-700 font-medium' : 'text-slate-600'}`}>
              <span>{s.label}</span>
              {selected.includes(s.code) && <Check size={14} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main View ───────────────────────────────────────────────────────────────
const ClientsView: React.FC = () => {
  const { id: selectedId } = useParams();
  const navigate = useNavigate();
  const { org } = useOrganization();
  const { user } = useAuth();
  const { clients, isLoading, createClient, updateClient, deleteClient, deleteContact, addContact, updateContact } = useClients();
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [sizeFilter, setSizeFilter] = useState<string[]>([]);
  const [sectorFilter, setSectorFilter] = useState<string[]>([]);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [hasMissionsFilter, setHasMissionsFilter] = useState(false);

  const hasActiveFilters = sizeFilter.length > 0 || sectorFilter.length > 0 || hasMissionsFilter;

  const filtered = useMemo(() => {
    let result = clients;

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.sector?.toLowerCase().includes(q) ||
        c.tags?.some(t => t.toLowerCase().includes(q)) ||
        c.contacts?.some(cc => cc.full_name.toLowerCase().includes(q))
      );
    }

    if (sizeFilter.length > 0)
      result = result.filter(c => c.categorie_entreprise && sizeFilter.includes(c.categorie_entreprise));

    if (sectorFilter.length > 0)
      result = result.filter(c => c.sector && sectorFilter.includes(c.sector));

    if (hasMissionsFilter)
      result = result.filter(c => (c.mission_count || 0) > 0);

    return [...result].sort((a, b) =>
      sortOrder === 'asc'
        ? a.name.localeCompare(b.name, 'fr')
        : b.name.localeCompare(a.name, 'fr')
    );
  }, [clients, search, sizeFilter, sectorFilter, sortOrder, hasMissionsFilter]);

  const selectedClient = useMemo(() => {
    if (!selectedId) return null;
    return clients.find(c => c.id === selectedId) || null;
  }, [clients, selectedId]);

  const handleCreate = async (data: any) => {
    if (!org) return;
    await createClient({ ...data, organization_id: org.id, created_by: user?.id || null });
    toast.success('Client créé');
  };

  const clearFilters = () => { setSizeFilter([]); setSectorFilter([]); setHasMissionsFilter(false); };

  if (selectedClient) {
    return (
      <ClientDetailPanel
        client={selectedClient}
        onClose={() => navigate('/clients')}
        onDelete={async (id) => { await deleteClient(id); navigate('/clients'); }}
        onUpdate={updateClient}
        onDeleteContact={async (id) => { await deleteContact(id); toast.success('Contact supprimé'); }}
        onAddContact={addContact}
        onEditContact={updateContact}
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2.5">
            <div className="p-2 bg-amber-100 rounded-xl">
              <Building2 size={22} className="text-amber-700" />
            </div>
            Répertoire Clients
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {hasActiveFilters || search
              ? <>{filtered.length} / {clients.length} client{clients.length > 1 ? 's' : ''}</>
              : <>{clients.length} client{clients.length > 1 ? 's' : ''} dans votre organisation</>
            }
          </p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-sm font-semibold transition-all hover:shadow-lg active:scale-95">
          <Plus size={16} /> Nouveau Client
        </button>
      </div>

      {/* Search + Filters */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col lg:flex-row gap-4 lg:items-center">
        {/* Search input */}
        <div className="relative flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Search className="text-amber-500" size={18} />
          </div>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par nom, secteur, contact..."
            className="w-full pl-10 pr-10 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all text-sm text-amber-700 placeholder-slate-400" />
          {search && (
            <div className="absolute inset-y-0 right-3 flex items-center">
              <button onClick={() => setSearch('')} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
            </div>
          )}
        </div>

        {/* Separator */}
        <div className="hidden lg:block w-px h-8 bg-slate-200"></div>

        {/* Filters group */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Sort toggle */}
          <button
            onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border bg-white text-slate-700 border-slate-200 hover:bg-slate-50 transition-colors"
          >
            <ArrowUpDown size={14} />
            {sortOrder === 'asc' ? 'A → Z' : 'Z → A'}
          </button>

          {/* Size multi-select */}
          <SizeFilterDropdown selected={sizeFilter} onChange={setSizeFilter} />

          {/* Sector multi-select */}
          <SectorFilterDropdown selected={sectorFilter} onChange={setSectorFilter} />

          {/* Avec missions */}
          <button
            onClick={() => setHasMissionsFilter(v => !v)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
              hasMissionsFilter
                ? 'bg-amber-50 text-amber-700 border-amber-300'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
            }`}
          >
            <Briefcase size={14} /> Avec missions
          </button>

          {/* Reset */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-slate-400 hover:text-red-500 text-sm font-medium transition-colors ml-2 animate-in fade-in"
            >
              <Trash2 size={14} />
              Réinitialiser
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-amber-200 border-t-amber-600 rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-2xl flex items-center justify-center">
            <Building2 size={28} className="text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-1">Aucun client trouvé</h3>
          <p className="text-sm text-slate-500 mb-4">
            {hasActiveFilters || search ? 'Essayez avec d\'autres filtres.' : 'Créez votre premier client pour commencer.'}
          </p>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-sm text-amber-600 hover:text-amber-700 font-medium">
              Effacer les filtres
            </button>
          )}
          {!hasActiveFilters && !search && (
            <button onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-sm font-semibold transition-all">
              <Plus size={16} /> Créer un client
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(client => (
            <ClientCard key={client.id} client={client} isActive={client.id === selectedId}
              onClick={() => navigate(client.id === selectedId ? '/clients' : `/clients/${client.id}`)} />
          ))}
        </div>
      )}

      {showCreate && <CreateClientModal onClose={() => setShowCreate(false)} onCreate={handleCreate} />}
    </div>
  );
};

export default ClientsView;
